import "./LabelingRulesModal.scss";
import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Modal, Form } from "react-bootstrap";
import { MdSmsFailed } from "react-icons/md";
import * as R from "ramda";
import { SegmentationMappingLabelRule } from "@blisspointmedia/bpm-types/dist/SegmentationMapping";
import {
  basicToAdvanced,
  Button,
  ButtonType,
  ButtonFrameworkVariant,
  Dropdown,
  DropdownToggleType,
  TextToggleButton,
} from "../Components";
import AlertDialog, { AlertDialogVariants } from "../Components/AlertDialog/AlertDialog";
import { makeFilter } from "../utils/boolean-logic-parser-creator";
import { FilterPaneAdvanced } from "../SingleChannel/FetchFilterBar/FilterPaneAdvanced";
import { CustomSegmentsData } from "./SegmentationMapping";
import {
  getFilterBarOptions,
  filterStringToOptions,
  getCategories,
  filterTokensToString,
} from "./segmentationMappingUtils";
import { SubBrand1 } from "../utils/colors";
import { SystemGeneratedSegmentNames } from "./CustomSegments";

interface LabelingRulesModalProps {
  rule?: SegmentationMappingLabelRule | undefined;
  customSegments: CustomSegmentsData[] | undefined;
  isNew: boolean;
  show: boolean;
  setShow: (show: boolean) => void;
  onRuleSave: (rule: SegmentationMappingLabelRule) => void;
  rules?: SegmentationMappingLabelRule[];
  dataGranularity?: "ad" | "ad_group" | "campaign";
  data: any;
  activeTab?: string;
}

const LabelingRulesModal = ({
  customSegments,
  isNew,
  show,
  setShow,
  rule,
  onRuleSave,
  dataGranularity = "campaign",
  rules = [],
  data,
  activeTab,
}: LabelingRulesModalProps): JSX.Element => {
  const [advanced, setAdvanced] = useState<boolean>(true);
  const [segmentName, setSegmentName] = useState<string>("");
  const [segmentId, setSegment] = useState<number>(0);
  const [advancedFilter, setAdvancedFilter] = useState<string>("");
  const [basicFilter, setBasicFilter] = useState<string>("");
  const [segmentValueId, setSegmentValue] = useState<number>(0);
  const [segmentValueName, setSegmentValueName] = useState<string>("");
  const setEditing = useState(false)[1];
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertModalSettings, setAlertModalSettings] = useState<{
    title: string;
    body: string;
    icon: React.ReactNode;
    variant: AlertDialogVariants;
    primaryButtonLabel: string;
    primaryAction: () => void;
    secondaryAction?: () => void;
    secondaryButtonLabel?: string;
  }>({
    title: "",
    body: "",
    icon: <></>,
    variant: AlertDialogVariants.INFO,
    primaryButtonLabel: "",
    primaryAction: () => {},
    secondaryAction: () => {},
    secondaryButtonLabel: "",
  });

  const isSegmentLocked = useMemo(() => {
    if (!activeTab || activeTab === "all") {
      return false;
    }
    return customSegments?.some(segment => segment.segmentName === activeTab);
  }, [activeTab, customSegments]);

  useEffect(() => {
    if (!show || !customSegments || activeTab === "all" || !activeTab) {
      return;
    }

    const matchedSegment = customSegments.find(segment => segment.segmentName === activeTab);

    if (matchedSegment) {
      setSegment(Number(matchedSegment.segmentId));
      setSegmentName(matchedSegment.segmentName);
    } else {
      setSegment(0);
      setSegmentName("");
    }
    setSegmentValue(0);
    setSegmentValueName("");
  }, [activeTab, show, customSegments]);

  const filterBarOptions = useMemo(() => {
    // custom segments for the filter bar only include Channel and Platform (IDs 1 and 2), the rest are not needed for the filter bar
    const segments = customSegments?.filter(segment => Number(segment.segmentId) <= 2);
    return getFilterBarOptions(segments, dataGranularity);
  }, [customSegments, dataGranularity]);

  const categories = useMemo(() => getCategories(filterBarOptions, data, customSegments || []), [
    customSegments,
    data,
    filterBarOptions,
  ]);

  const baseRule = useMemo(() => {
    if (rule) {
      return rule;
    }
    return ({
      filterTokens: [],
      setSegmentId: 0,
      setSegmentValueId: 0,
      isAdvanced: true,
      isActive: true,
    } as unknown) as Partial<SegmentationMappingLabelRule>;
  }, [rule]);

  useEffect(() => {
    if (rule && show) {
      setAdvanced(rule.isAdvanced);
      setSegment(rule.setSegmentId);
      setSegmentName(
        customSegments?.find(segment => Number(segment.segmentId) === rule.setSegmentId)
          ?.segmentName || ""
      );
      setSegmentValue(rule.setSegmentValueId);
      setSegmentValueName(
        customSegments
          ?.find(segment => Number(segment.segmentId) === rule.setSegmentId)
          ?.values.find(value => Number(value.valueId) === rule.setSegmentValueId)?.valueName || ""
      );
      if (rule.isAdvanced) {
        setAdvancedFilter(
          filterTokensToString((rule.filterTokens as unknown) as any[], categories, true)
        );
      } else {
        setBasicFilter(
          filterTokensToString((rule.filterTokens as unknown) as any[], categories, false)
        );
      }
    }
  }, [rule, show, customSegments, categories]);

  const filterPane = useMemo(() => {
    if (advanced) {
      return (
        <div className="advancedFilterPane">
          <div className="label">If these conditions exist:</div>
          <FilterPaneAdvanced
            categories={categories}
            compileError=""
            setEditing={setEditing}
            setState={setAdvancedFilter}
            state={advancedFilter}
            showAdvancedHeader={false}
          />
        </div>
      );
    }
    const granularityHeader = {
      ad: "Campaign, Ad Group, or Ad includes:",
      ad_group: "Campaign or Ad Group includes:",
      campaign: "Campaign includes:",
    }[dataGranularity];
    return (
      <div className="basicFilterPane">
        <div className="label">{granularityHeader}</div>
        <Form.Control
          type="text"
          placeholder="e.g. _Brand_"
          value={basicFilter}
          onChange={e => {
            setBasicFilter(e.currentTarget.value);
          }}
        ></Form.Control>
      </div>
    );
  }, [advanced, dataGranularity, basicFilter, categories, setEditing, advancedFilter]);

  const segmentOptions = useMemo(() => {
    const options = customSegments
      ?.filter(segment => {
        const systemNames = Object.values(SystemGeneratedSegmentNames).map(name => String(name));
        return !systemNames.includes(segment.segmentName);
      })
      .map(segment => ({
        label: segment.segmentName,
        value: segment.segmentId,
        segmentValues: segment.values.map(value => ({
          label: value.valueName,
          value: value.valueId,
        })),
      }));
    return options;
  }, [customSegments]);

  const segmentValueOptions = useMemo(() => {
    if (segmentId === 0) {
      return [];
    }
    const options = segmentOptions?.find(option => Number(option.value) === segmentId)
      ?.segmentValues;
    return options;
  }, [segmentId, segmentOptions]);

  const filterTokens = useCallback(() => {
    let tokens;
    if (advanced) {
      tokens = filterStringToOptions(advancedFilter, filterBarOptions, data);
    } else {
      tokens = [{ label: basicFilter, value: basicFilter }];
    }
    return tokens;
  }, [advanced, advancedFilter, basicFilter, data, filterBarOptions]);

  const validateFilter = useMemo(() => {
    try {
      // we don't need to validate if there are no tokens, that is handled in the missingFields check
      if (!rule?.filterTokens || !filterTokens().length) {
        return "";
      }
      let tokens = filterTokens();
      if (!advanced) {
        tokens = basicToAdvanced(tokens, filterBarOptions);
      }
      makeFilter(tokens, filterBarOptions);
      return "";
    } catch (e) {
      const error = e as Error;
      if (error.message.includes("You can't end with an operation")) {
        return "An invalid keyword or no keyword was entered.";
      }
      return (e as Error).message;
    }
  }, [advanced, filterTokens, filterBarOptions, rule]);

  const isDuplicateRule = useMemo(() => {
    return rules?.some(
      r =>
        R.equals(r.filterTokens, filterTokens()) &&
        r.setSegmentId === Number(segmentId) &&
        r.id !== rule?.id
    );
  }, [filterTokens, rule, rules, segmentId]);

  const isDuplicateNewRule = useMemo(() => {
    return rules.some(
      r =>
        R.equals(r.filterTokens, filterTokens()) &&
        r.setSegmentId === Number(segmentId) &&
        r.id === undefined &&
        r.createdAt !== rule?.createdAt
    );
  }, [filterTokens, rule?.createdAt, rules, segmentId]);

  const missingFields = useMemo(() => {
    const filter = filterTokens();
    return [
      (!filter || !filter.length || (!basicFilter && !advanced)) && "A valid filter must be set",
      !segmentId && "A segment must be selected",
      !segmentValueId && "A segment value must be selected",
    ].filter(Boolean) as string[];
  }, [advanced, basicFilter, filterTokens, segmentId, segmentValueId]);

  const segmentValueMismatch = useMemo(() => {
    const segment = customSegments?.find(s => Number(s.segmentId) === segmentId);
    return !segment?.values.some(value => value.valueId === segmentValueId);
  }, [customSegments, segmentId, segmentValueId]);

  const resetRule = () => {
    setBasicFilter("");
    setAdvancedFilter("");
    setSegmentName("");
    setSegment(0);
    setSegmentValue(0);
    setSegmentValueName("");
    setAdvanced(true);
  };

  const handleCancel = () => {
    resetRule();
    setShow(false);
  };

  const modifiedRule = useCallback(() => {
    return {
      ...baseRule,
      filterTokens: filterTokens(),
      setSegmentId: Number(segmentId),
      setSegmentValueId: Number(segmentValueId),
      isAdvanced: advanced,
    };
  }, [baseRule, filterTokens, segmentId, segmentValueId, advanced]);

  const handleSave = () => {
    const commonModalSettings = {
      icon: <MdSmsFailed color={SubBrand1} size={75} />,
      variant: AlertDialogVariants.ERROR,
      primaryButtonLabel: "Back",
      primaryAction: () => setShowAlertModal(false),
      secondaryAction: undefined,
      secondaryButtonLabel: undefined,
    };

    if (validateFilter) {
      setAlertModalSettings({
        ...commonModalSettings,
        title: "Invalid Filter",
        body: validateFilter,
      });
      setShowAlertModal(true);
      return;
    }

    if (isDuplicateRule || isDuplicateNewRule) {
      setAlertModalSettings({
        ...commonModalSettings,
        title: "Duplicate Rule",
        body:
          "A rule already exists with this filter and selected segment, please change the filter or selected segment.",
      });
      setShowAlertModal(true);
      return;
    }

    if (missingFields.length > 0) {
      setAlertModalSettings({
        ...commonModalSettings,
        title: "Missing Fields",
        body: missingFields.join(". "),
      });
      setShowAlertModal(true);
      return;
    }

    if (segmentId && segmentValueId && segmentValueMismatch) {
      setAlertModalSettings({
        ...alertModalSettings,
        title: "Segment Value Mismatch",
        body:
          "The selected segment value does not match the selected segment, please change the segment value or segment.",
      });
      setShowAlertModal(true);
      return;
    }

    const ruleToSave = modifiedRule();

    onRuleSave(ruleToSave as SegmentationMappingLabelRule);

    resetRule();
    setShow(false);
  };

  const handleToggleChange = (option: string) => {
    const toggledAdvanced = option === "Advanced";
    if (toggledAdvanced) {
      setBasicFilter("");
    } else {
      setAdvancedFilter("");
    }
    setAdvanced(toggledAdvanced);
  };

  return (
    <>
      <AlertDialog
        title={alertModalSettings?.title}
        body={alertModalSettings?.body}
        icon={alertModalSettings?.icon}
        variant={alertModalSettings?.variant}
        primaryButtonLabel={alertModalSettings?.primaryButtonLabel}
        primaryAction={alertModalSettings?.primaryAction}
        show={showAlertModal}
        onHide={() => setShowAlertModal(false)}
        secondaryButtonLabel={
          alertModalSettings?.secondaryButtonLabel
            ? alertModalSettings?.secondaryButtonLabel
            : undefined
        }
        secondaryAction={
          alertModalSettings?.secondaryAction ? alertModalSettings?.secondaryAction : undefined
        }
      />
      <Modal show={show} onHide={() => setShow(false)} className="labelingRulesModal" size="lg">
        <Modal.Header>
          <div className="titleContainer">
            <Modal.Title>
              {isNew ? "New" : "Edit"} Labeling Rule {!isNew && rule ? rule.name : ""}
            </Modal.Title>
          </div>

          <TextToggleButton
            options={["Basic", "Advanced"]}
            selectedOption={advanced ? "Advanced" : "Basic"}
            onChange={handleToggleChange}
          />
        </Modal.Header>
        <Modal.Body>
          <div className="filterSelect">{filterPane}</div>
          <div className="segmentSelectionContainer">
            <div className="dropdown segmentSelect">
              {isSegmentLocked ? (
                <div className="label">{`Then set ${segmentName}`}</div>
              ) : (
                <>
                  <div className="label">Then set this segment:</div>
                  <Dropdown
                    type={DropdownToggleType.OUTLINED}
                    className="segmentDropdown"
                    hasFilterBar
                    placeholder={"Select Segment"}
                    options={segmentOptions?.map(op => op.label) || []}
                    value={segmentName}
                    onChange={value => {
                      setSegmentName(value);
                      const id = segmentOptions?.find(op => op.label === value)?.value;
                      setSegment(Number(id));
                      setSegmentValue(0);
                      setSegmentValueName("");
                    }}
                  />
                </>
              )}
            </div>
            <div className="dropdown">
              <div className="label">To this value:</div>
              <Dropdown
                type={DropdownToggleType.OUTLINED}
                className="segmentDropdown"
                hasFilterBar
                placeholder={"Select Segment Value"}
                options={segmentValueOptions?.map(op => op.label) || []}
                value={segmentValueName}
                onChange={v => {
                  setSegmentValueName(v);
                  const id = segmentValueOptions?.find(op => op.label === v)?.value;
                  setSegmentValue(Number(id));
                }}
                disabled={!segmentId}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="cancelButton"
            type={ButtonType.OUTLINED}
            variant={ButtonFrameworkVariant.NO_ICON}
            onClick={() => handleCancel()}
          >
            Cancel
          </Button>
          <Button
            className="cancelButton"
            type={ButtonType.FILLED}
            variant={ButtonFrameworkVariant.NO_ICON}
            onClick={() => handleSave()}
          >
            {isNew ? "Add Rule" : "Update Rule"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default LabelingRulesModal;
